import { Link, useSearchParams } from 'react-router-dom';

import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import type { JobSavedResult } from '@/modules/hiring-manager/chat/chatResults';
import { toSearchSuffix } from '@/modules/hiring-manager/utils/searchSuffix';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import { Button } from '@/modules/ui/components/button/Button';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';

interface SavedJobProps {
  saved: JobSavedResult;
  /** Offered when the HM asked to edit this job themselves and closed the editor. */
  onEdit?: () => void;
}

/**
 * A job the chat created or saved, as it is now, with the way to it. On the Managed plan the HM's Finlay recruiter
 * makes the changes once a job exists, so a draft offers no editor: the edit page is closed to the HM.
 */
export function SavedJob({ saved, onEdit }: SavedJobProps) {
  const [searchParams] = useSearchParams();
  const searchSuffix = toSearchSuffix(searchParams);
  const { plan } = useWorkspacePlan();
  const jobQuery = useJobQuery(saved.jobId);
  // The job may have moved on since the reply, such as a draft published from the editor.
  const isDraft =
    (jobQuery.data?.sourcingState ?? saved.sourcingState) === 'Draft';
  const managed = plan === 'managed';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-neutral-500">
          {isDraft ? 'Saved as a draft' : 'Saved'}
        </span>
        {jobQuery.isPending ? (
          <Skeleton className="h-6 w-48 bg-neutral-200" />
        ) : (
          <h2 className="text-[17px] leading-6 font-semibold tracking-[-0.01em]">
            {jobQuery.data?.title ?? 'Your job'}
          </h2>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {onEdit && !managed && (
          <Button onClick={onEdit} size="sm">
            Edit manually
          </Button>
        )}
        {!(isDraft && managed) && (
          <Button asChild size="sm" variant="outline">
            <Link
              to={
                isDraft
                  ? `/hm/jobs/${saved.jobId}/edit${searchSuffix}`
                  : `/hm/jobs/${saved.jobId}/candidates${searchSuffix}`
              }
            >
              {isDraft ? 'Open draft' : 'View candidates'}
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}
